// Synthesizes victory fanfare sound using Web Audio API

let audioCtx: AudioContext | null = null;
let masterGain: GainNode | null = null;
let soundsPlayed = false;

function ensureAudio(): AudioContext {
  if (!audioCtx) {
    const AudioContextClass = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audioCtx = new AudioContextClass();
    masterGain = audioCtx.createGain();
    masterGain.gain.value = 1;
    masterGain.connect(audioCtx.destination);
  }
  return audioCtx;
}

function tone(t0: number, opts: { freq: number; slideTo?: number; slideDur?: number; dur: number; type?: OscillatorType; peak?: number }) {
  if (!audioCtx || !masterGain) return;
  const o = audioCtx.createOscillator();
  o.type = opts.type || 'sine';
  o.frequency.setValueAtTime(opts.freq, t0);
  if (opts.slideTo) {
    o.frequency.exponentialRampToValueAtTime(opts.slideTo, t0 + (opts.slideDur || opts.dur));
  }
  const g = audioCtx.createGain();
  const peak = opts.peak != null ? opts.peak : 0.16;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.015);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + opts.dur);
  o.connect(g);
  g.connect(masterGain);
  o.start(t0);
  o.stop(t0 + opts.dur + 0.03);
}

function noiseBurst(t0: number, opts: { dur: number; freqFrom: number; freqTo: number; peak?: number }) {
  if (!audioCtx || !masterGain) return;
  const dur = opts.dur;
  const sampleRate = audioCtx.sampleRate;
  const buf = audioCtx.createBuffer(1, Math.max(1, Math.floor(sampleRate * dur)), sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
  }
  const src = audioCtx.createBufferSource();
  src.buffer = buf;
  const filt = audioCtx.createBiquadFilter();
  filt.type = 'bandpass';
  filt.Q.value = 0.9;
  filt.frequency.setValueAtTime(opts.freqFrom, t0);
  filt.frequency.exponentialRampToValueAtTime(opts.freqTo, t0 + dur);
  const g = audioCtx.createGain();
  const peak = opts.peak != null ? opts.peak : 0.22;
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(peak, t0 + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filt);
  filt.connect(g);
  g.connect(masterGain);
  src.start(t0);
  src.stop(t0 + dur + 0.03);
}

function startNotes(ctx: AudioContext) {
  const t0 = ctx.currentTime + 0.03;
  tone(t0, { freq: 180, slideTo: 90, dur: 1.1, type: 'sine', peak: 0.05 });
  tone(t0 + 0.14, { freq: 150, slideTo: 210, dur: 0.75, type: 'sine', peak: 0.07 });
  noiseBurst(t0 + 0.30, { dur: 0.5, freqFrom: 2600, freqTo: 500, peak: 0.22 });
  tone(t0 + 0.32, { freq: 900, slideTo: 300, dur: 0.4, type: 'triangle', peak: 0.10 });
  tone(t0 + 0.50, { freq: 523, dur: 0.14, type: 'square', peak: 0.09 });
  tone(t0 + 0.58, { freq: 659, dur: 0.14, type: 'square', peak: 0.09 });
  tone(t0 + 0.66, { freq: 784, dur: 0.22, type: 'square', peak: 0.10 });
  tone(t0 + 0.72, { freq: 520, dur: 0.10, type: 'sine', peak: 0.09 });
  tone(t0 + 0.80, { freq: 600, dur: 0.10, type: 'sine', peak: 0.09 });
  tone(t0 + 0.88, { freq: 680, dur: 0.10, type: 'sine', peak: 0.09 });
  tone(t0 + 0.96, { freq: 760, dur: 0.12, type: 'sine', peak: 0.10 });
  tone(t0 + 1.05, { freq: 784, dur: 0.18, type: 'triangle', peak: 0.12 });
  tone(t0 + 1.16, { freq: 1046, dur: 0.35, type: 'triangle', peak: 0.13 });
}

export function playIntroFanfare(): void {
  if (soundsPlayed) return;

  const playNow = () => {
    if (soundsPlayed) return;
    try {
      const ctx = ensureAudio();
      if (ctx.state === 'suspended') {
        ctx.resume().then(() => {
          if (ctx.state === 'running' && !soundsPlayed) {
            soundsPlayed = true;
            startNotes(ctx);
          }
        }).catch(() => {});
      } else if (ctx.state === 'running' && !soundsPlayed) {
        soundsPlayed = true;
        startNotes(ctx);
      }
    } catch (e) {
      console.warn('Web Audio Playback Error:', e);
    }
  };

  // Check if browser allows autoplay (user has interacted with the page/domain previously)
  if (navigator.userActivation?.hasBeenActive) {
    playNow();
    return;
  }

  // Otherwise, attach gesture listeners to create & play AudioContext ONLY upon user interaction
  const unlock = () => {
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('click', unlock);
    window.removeEventListener('keydown', unlock);
    window.removeEventListener('touchstart', unlock);
    playNow();
  };

  window.addEventListener('pointerdown', unlock, { once: true });
  window.addEventListener('click', unlock, { once: true });
  window.addEventListener('keydown', unlock, { once: true });
  window.addEventListener('touchstart', unlock, { once: true });
}

export function resetSoundEngine(): void {
  soundsPlayed = false;
}
